import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NavLink, Link } from "react-router-dom";

import { toggleModalDiagramShare, diagramShare, enablePricingPlanLimit } from '../../../redux/main/main.actions';
import { isShareRoleAllowed } from './shareRoles';
import { isValidInviteEmail, isNewWorkspaceEmail, isExistingMember, isPendingInvite } from './shareInvite';
import { usePlanGate } from '../../../hooks/usePlanGate';
import { LockBadge } from '../../PlanGate/ProGate';
import { selectIsModalDiagramShareEnabled, selectModalDiagramShareData } from '../../../redux/main/main.selectors';
import { selectAuthUser } from '../../../redux/auth/auth.selectors';

import {
    fetchDiagramMembers,
    changeRoleForDiagramMember,
    removeDiagramMember,
    fetchDiagramInvites,
    createDiagramInvite,
    revokeDiagramInvite,
} from '../../../redux/canvas/canvas.actions';
import { selectDiagramMembers, selectDiagramInvites } from '../../../redux/canvas/canvas.selectors';

import { selectWorkspaceMembers, selectCurrentWorkspace, selectWorkspacePlan } from '../../../redux/workspace/workspace.selectors';
import { workspaceFetchMembersRequest } from '../../../redux/workspace/workspace.actions';

import { showNotify } from '../../../redux/notify/notify.actions';

import Modal from '../Modal/Modal';
import LoaderSimple from '../../Loaders/LoaderSimple/LoaderSimple';

import { trackEvent } from '../../../lib/analytics';
import { getAvatarUrl } from '../../../utils/get-avatar-url';

import './ModalDiagramShare.scss';
import ButtonDropdown from '../../../pages/MainApp/_components/ButtonDropdown/ButtonDropdown';
import ButtonDropdownInvite from '../../../pages/MainApp/_components/ButtonDropdown/ButtonDropdownInvite';

import { copyToClipboard } from '../../../utils/copy-to-clipboard';

function buildViewMode(accessRole = 'view') {
    switch (accessRole) {
        case 'view':
        case 'comment':
            return 'type_1';
        case 'edit':
            return 'type_2';
        case 'admin':
        case 'owner':
            return 'type_3';
        default:
            return 'type_1';
    }
}

function canChangeRole(yourRole, oldMemberRole, newMemberRole) {
    return (
        (yourRole === 'owner' && oldMemberRole !== 'owner' && newMemberRole !== 'owner')
        || (yourRole === 'admin' && !['owner', 'admin'].includes(oldMemberRole) && !['owner', 'admin'].includes(newMemberRole))
    );
}

export const ModalDiagramShare = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // const { can } = usePlanGate();
    const { can, requireUpgrade, notifyCap } = usePlanGate();

    const isOpen = useSelector(selectIsModalDiagramShareEnabled);
    const modalData = useSelector(selectModalDiagramShareData);

    const diagramMembers = useSelector(selectDiagramMembers);
    const diagramInvites = useSelector(selectDiagramInvites);

    const diagramInvitesCount = Array.isArray(diagramInvites) ? diagramInvites.length : 0;

    // const diagramInvites = [
    // {
    //     id: 123,
    //     role: 'view',
    //     email: 'test@email.com',
    // },
    // ];

    const workspacePlan = useSelector(selectWorkspacePlan) || 'free';
    const isFreePlan = workspacePlan === 'free';

    const currentUser = useSelector(selectAuthUser);

    // The diagram being shared (full object stored on the modal payload), the
    // current user's role on it, and the current user's id — all from real state.
    // The caller's role field differs by source: the diagrams *list* (main page)
    // exposes `access_role`, while the single-diagram *GET* (canvas page,
    // `diagramDetailsCanvas`) exposes `my_role`. Read both so the canvas modal
    // resolves owner/admin instead of falling back to 'view'.
    const diagramId = modalData?.diagram?.id || modalData?.id || null;
    const yourRole = modalData?.diagram?.access_role || modalData?.diagram?.my_role || 'view';
    const yourId = currentUser?.id ?? null;

    // Default role chosen for new invites. On free plan we default to 'edit'
    // (the only enabled option, shown to the user as 'Member').
    // const initialRole = isFreePlan ? 'member' : 'view';
    const initialRole = isFreePlan ? 'edit' : 'view';

    const [selectedRole, setSelectedRole] = useState(initialRole);
    const [inviteValue, setInviteValue] = useState('');

    const inputRef = useRef(null);
    const [viewType, setViewType] = useState('type_1');
    const [isLinkCopied, setIsLinkCopied] = useState(false);

    const [diagramTitle, setDiagramTitle] = useState('');

    useEffect(() => {
        if (!modalData?.diagram?.id) return;

        dispatch(fetchDiagramMembers(modalData?.diagram?.id));
        setViewType(buildViewMode(yourRole));
        setDiagramTitle(modalData?.diagram?.title || '');
        setSelectedRole(initialRole);

        if (yourRole === 'owner') {
            dispatch(fetchDiagramInvites(modalData?.diagram?.id));
        }

        return () => { };
    }, [modalData, initialRole]);

    const handleCancel = () => {
        dispatch(toggleModalDiagramShare(null));
        setTimeout(() => {
            setInviteValue('');
            setIsLinkCopied(false);

            setSelectedRole(initialRole);
            setDiagramTitle('');
        }, 300);
    };

    const handleSave = () => {
        // dispatch(diagramShare(modalData?.diagram?.id, inviteValue));
        dispatch(toggleModalDiagramShare(null));

        setTimeout(() => {
            setInviteValue('');
            setIsLinkCopied(false);

            setSelectedRole(initialRole);
        }, 300);
    };

    // const handleKeyDown = (e) => {
    //     if (e.key === 'Enter') {
    //         e.preventDefault();
    //         handleSave();
    //     }
    // };

    const handleRender = () => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleCopyUsername = async (e, value) => {
        // window.open("/settings/profile?view=position", "_blank", "noopener,noreferrer");
        // window.open("/settings/profile?view=position", "_blank");

        e?.preventDefault?.();
        const ok = await copyToClipboard(value);
        if (ok) {
            dispatch(showNotify({ message: 'username_copied' }));
        } else {
            // dispatch(showNotify({ message: 'code_copy_failed' }));
        }

    };


    // const handleCopyDiagramLinkClick = async (e) => {
    //     const baseUrl = window.location.origin;
    //     const path = `/d/${modalData?.diagram?.id}`;
    //     const fullUrl = `${baseUrl}${path}`;

    //     try {
    //         await navigator.clipboard.writeText(fullUrl);
    //         dispatch(showNotify({ message: 'link_copied' }));
    //     } catch (err) { }

    //     if (!isLinkCopied) {
    //         setIsLinkCopied(true);
    //     }
    // };
    const handleCopyDiagramLinkClick = async (e) => {
        trackEvent('app_modal_diagram_share_copy_link', {
            page: 'app',
            type: 'modals',
        });

        const baseUrl = window.location.origin;
        const path = `/d/${modalData?.diagram?.id}`;
        const fullUrl = `${baseUrl}${path}`;

        e?.preventDefault?.();
        const ok = await copyToClipboard(fullUrl);
        if (ok) {
            dispatch(showNotify({ message: 'link_copied' }));
        } else {
            // dispatch(showNotify({ message: 'link_copy_failed' }));
        }

        if (!isLinkCopied) {
            setIsLinkCopied(true);
        }
    };


    const handleInvite = (email, role, callbacks) => {
        trackEvent('app_modal_diagram_share_invite_submit', {
            page: 'app',
            type: 'modals',
            role,
        });

        dispatch(createDiagramInvite(diagramId, email, role, callbacks));
    };

    const handleChangeMemberRole = (memberId, newRole) => {
        const member = diagramMembers.find(member => member.id == memberId);
        if (member && member.role !== newRole) {
            trackEvent('app_modal_diagram_share_change_role_submit', {
                page: 'app',
                type: 'modals',
                oldRole: member.role,
                newRole,
            });

            const canChangeMemberRole = canChangeRole(yourRole, member.role, newRole);

            if (canChangeMemberRole) {
                dispatch(changeRoleForDiagramMember(diagramId, memberId, newRole));
            }
        }
    };

    const handleRemoveMember = (memberId) => {
        const member = diagramMembers.find(member => member.id == memberId);
        if (member) {
            const canChangeMemberRole = canChangeRole(yourRole, member.role, member.role);
            if (canChangeMemberRole) {
                dispatch(removeDiagramMember(diagramId, member.user_id));
            }
        }
    };

    const handleRevokeInvite = (inviteId) => {
        const invite = diagramInvites.find(invite => invite.id == inviteId);
        if (invite) {
            dispatch(revokeDiagramInvite(diagramId, inviteId));
        }
    };

    const handleChangeRoleForInvite = (inviteId) => {
        if (!isShareRoleAllowed(workspacePlan, '')) {
            notifyCap('admin_roles', {}, modalData?.page);
            return;
        }

        // const invite = diagramInvites.find(invite => invite.id == inviteId);
        // if (invite) {
        //     dispatch(revokeDiagramInvite(diagramId, inviteId));
        // }
    };



    // // own => ['view', 'comment', 'edit', 'editadmin'] + can remove members
    // const menuItemsOwn = [
    //     // TODO: editadmin
    //     // {
    //     //     role: 'editadmin',
    //     //     onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     // },
    //     {
    //         role: 'edit',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    //     {
    //         role: 'comment',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    //     {
    //         role: 'view',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    //     {
    //         line: true,
    //     },
    //     {
    //         role: 'remove',
    //         onClick: (memberId) => { handleRemoveMember(memberId); },
    //     },
    // ];

    // // editadmin => ['view', 'comment', 'edit'] + can not to remove members
    // const menuItemsEdit = [
    //     {
    //         role: 'edit',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    //     {
    //         role: 'comment',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    //     {
    //         role: 'view',
    //         onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
    //     },
    // ];

    const menuItemsInvited = [
        {
            locked: true,
            role: 'change_role',
            onClick: (inviteId) => { handleChangeRoleForInvite(inviteId); },
        },
        {
            role: 'remove_invite',
            onClick: (inviteId) => { handleRevokeInvite(inviteId); },
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            modalClass={`modal-share ${modalData?.page === 'main' ? 'modal-share-main' : 'modal-share-canvas'} ${viewType}`}
            onCancel={handleCancel}
            onShouldRender={handleRender}
        >
            <div className="header">{t('m_diagram_share.header', { title: diagramTitle || '' })}</div>
            <div className="form-group one">
                {
                    ['type_1', 'type_2'].includes(viewType)
                        // ? <div className='info-text'>Only the diagram owner and admins can invite new members.</div>
                        // ? <div className='info-text'>Only the diagram owners can invite new members.</div>
                        // ? <div className='info-text'>Only diagram owners can invite members</div>
                        // ? <div className='info-text'>Only owner and admins can invite members</div>
                        ? <div className='info-text'>{t('m_diagram_share.owner_only_invite')}</div>
                        : <InviteInput isModalOpen={isOpen} selectedRole={selectedRole} setSelectedRole={setSelectedRole} value={inviteValue} setValue={setInviteValue} yourRole={yourRole} yourId={yourId} diagramMembers={diagramMembers} diagramInvites={diagramInvites} plan={workspacePlan} page={modalData?.page} onInvite={handleInvite} />
                }
            </div>

            {['type_2', 'type_3'].includes(viewType)
                ? <div className={`members-group ${diagramInvitesCount > 0 ? 'members-with-invites' : ''}`}>
                    {diagramMembers?.map((member, index) =>
                        <div key={`mem-item-${index}`} className='members-item'>
                            <div className="avatar">
                                {member.image
                                    ? <img className="avatar-image" src={getAvatarUrl(member.image, 52)} width="40px" height="40px" referrerPolicy="no-referrer" />
                                    : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="-0.5 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                }
                            </div>
                            {member.type === 'team'
                                ? <div className="dash-sidebar-profile-description">
                                    <span className="profile-name profile-team">{member.name}</span>
                                </div>
                                : <div className="dash-sidebar-profile-description">
                                    <span className="profile-name">{member.name} {member.user_id === yourId ? <span className="you">{t('m_diagram_share.you')}</span> : ''}</span>
                                    {/* <span className="profile-name">{member.name}</span> */}
                                    {member.title ? <span className="profile-email">{member.title}</span> : null}
                                    {!member.title && member.id === yourId ?
                                        // <NavLink
                                        //     to="/settings/profile?section=role"
                                        //     target="_blank"
                                        //     rel="noopener noreferrer"
                                        //     className="profile-email-empty"
                                        //     onClick={() => { handleOpenSettingsClick() }}
                                        // >
                                        // </NavLink>

                                        // <span className="profile-email-empty" onClick={() => { handleOpenSettingsClick() }}>Add your work position</span>
                                        <span className="profile-email-empty" onClick={(e) => { handleCopyUsername(e, '@username') }}>@username</span>
                                        : null}
                                </div>
                            }
                            {(yourRole === 'admin' && ['edit', 'comment', 'view'].includes(member.role) ||
                                yourRole === 'owner' && ['admin', 'edit', 'comment', 'view'].includes(member.role))
                                ? <ButtonDropdown
                                    memberId={member.id}
                                    currentRole={member.role}
                                    roles={['edit', 'comment', 'view']}
                                    onChangeRole={handleChangeMemberRole}
                                    onRemove={handleRemoveMember}
                                    changeRoleEnabled={can('admin_roles')}
                                    // changeRoleEnabled={true}
                                    canRemove={yourRole === 'owner'}
                                    plan={workspacePlan}
                                />
                                : <button tabIndex={-1} className={`dash-sidebar-logout ${member.role}`}>{buildRoleName(member.role, workspacePlan, t)}</button>
                            }
                        </div>
                    )}
                </div>
                : ''
            }
            {['type_3'].includes(viewType) && diagramInvitesCount > 0
                ? <div className="members-group">
                    {diagramInvites?.map((invite, index) =>
                        <div key={`mem-item-${index}`} className='members-item'>
                            <div className="avatar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="-0.5 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                            </div>
                            <div className="dash-sidebar-profile-description">
                                <span className="profile-name">{invite.email}</span>
                                {/* <span className="profile-email profile-text">Will be added to the workspace</span> */}
                                <span className="profile-email profile-text">{t('m_diagram_share.invited_to_workspace')}</span>
                            </div>
                            <ButtonDropdownInvite roleText={buildRoleName(invite.role, workspacePlan, t)} inviteId={invite.id} menuItems={menuItemsInvited} />
                        </div>
                    )}
                </div>
                : ''
            }

            {/* <div className='bottom-section'>
                <div className='bottom-section-left'>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 11V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V11M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15.8C21 14.1198 21 13.2798 20.673 12.638C20.3854 12.0735 19.9265 11.6146 19.362 11.327C18.7202 11 17.8802 11 16.2 11H7.8C6.11984 11 5.27976 11 4.63803 11.327C4.07354 11.6146 3.6146 12.0735 3.32698 12.638C3 13.2798 3 14.1198 3 15.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z"></path></svg>
                    <div className='bottom-section-left-text'>
                        <span className='bottom-section-left-text-title'>Protected</span>
                        <span className='bottom-section-left-text-subtitle'>Only users with access can open link</span>
                    </div>
                </div>
                <div className='bottom-section-right'>
                    {!isLinkCopied ?
                        <button className="dash-sidebar-logout link" onClick={(e) => handleCopyDiagramLinkClick(e)}>
                            <svg width="27px" height="27px" viewBox="0 0 24 24" fill="none" transform="rotate(-45)"><g><path d="M10 16H7C4.79086 16 3 14.2091 3 12V12C3 9.79086 4.79086 8 7 8H10" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M16 12H8" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M14 16H17C19.2091 16 21 14.2091 21 12V12C21 9.79086 19.2091 8 17 8H14" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                            Copy Link
                        </button>
                        : <button className="dash-sidebar-logout link copied" onClick={(e) => handleCopyDiagramLinkClick(e)}>
                            <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M4 12L8.94975 16.9497L19.5572 6.34326" stroke="#29d1ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                            Copied
                        </button>
                    }
                </div>
            </div> */}
        </Modal>
    );
};

export default ModalDiagramShare;

const ROLES_IDS = ['member', 'view', 'comment', 'edit', 'admin', 'owner', 'admin_roles'];

// Role display names live in the shared `roles` i18n group (also used by
// ButtonDropdown's own identical map) so both stay in sync.
function buildRoleName(role, plan = null, t) {
    // On the free plan we expose only a single role to the user, labeled
    // 'Member', even though it maps to the backend 'edit' role.
    // if (plan === 'free' && role === 'edit') {
    //     return t('roles.member');
    // }
    const key = ROLES_IDS.includes(role) ? role : 'view';
    return key === 'admin_roles' ? t('roles.more_roles') : t(`roles.${key}`);
}

export const InviteInput = ({ value, setValue, selectedRole, setSelectedRole, isModalOpen = false, yourRole = 'admin', yourId, diagramMembers = [], diagramInvites = [], plan = 'free', page = '', onInvite }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { requireUpgrade, notifyCap } = usePlanGate();

    const workspace = useSelector(selectCurrentWorkspace);
    const workspaceMembers = useSelector(selectWorkspaceMembers) || [];

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);
    const [isValidationStarted, setIsValidationStarted] = useState(false);
    const [needsFocus, setNeedsFocus] = useState(false);

    const inputRef = useRef(null);
    const btnMenuRef = useRef(null);
    const menuRef = useRef(null);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (btnMenuRef.current && !btnMenuRef.current.contains(e.target)
                && menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
            if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)
                && inputRef.current && !inputRef.current.contains(e.target)) {
                setIsAutocompleteOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isModalOpen && workspace?.id) {
            dispatch(workspaceFetchMembersRequest(workspace.id));
        }
    }, [isModalOpen, workspace?.id]);

    const handleMenuItemClick = (role) => {
        if (!isShareRoleAllowed(plan, role)) {
            // requireUpgrade('admin_roles');
            notifyCap('admin_roles', {}, page);
            setIsMenuOpen(false);
            return;
        }
        setSelectedRole(role);
        setIsMenuOpen(false);
    };

    const handleBtnMenu = () => {
        setIsMenuOpen(prevValue => {
            const next = !prevValue;
            if (next) setIsAutocompleteOpen(false);
            return next;
        });
        // if (!isMenuOpen && inputRef.current) {
        //     inputRef.current.focus();
        // }
    };

    useEffect(() => {
        if (isModalOpen && !isMenuOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isMenuOpen]);

    useEffect(() => {
        if (isInvalidEmail && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInvalidEmail]);

    // Refocus the input after a failed invite request, once it re-enables
    // (a synchronous focus() in the error handler would no-op on the still
    // disabled input).
    useEffect(() => {
        if (needsFocus && !isValidating && inputRef.current) {
            inputRef.current.focus();
            setNeedsFocus(false);
        }
    }, [needsFocus, isValidating]);

    useEffect(() => {
        if (isModalOpen && yourRole) {
            let values = [];

            if (plan === 'free') {
                // values = ['admin', 'edit', 'comment', 'view'];
                values = ['edit', 'admin_roles'];
                // values = ['member', 'admin_roles'];

                // Free plan: only 'edit' (displayed as 'Member') is enabled;
                // the other roles are shown disabled as an upsell hint.
                // values = ['member', 'line', 'edit', 'comment', 'view'];
                // values = ['admin', 'edit', 'comment', 'view'];

            } else if (yourRole === 'owner') {
                // TODO: editadmin
                values = ['admin', 'edit', 'comment', 'view'];
                // values = ['edit', 'comment', 'view'];
            } else if (yourRole === 'admin') {
                values = ['edit', 'comment', 'view'];
            }
            setMenuItems(values);
        }
    }, [yourRole, plan, isModalOpen]);

    const diagramMemberKeys = React.useMemo(() => {
        const emails = new Set();
        const userIds = new Set();
        (diagramMembers || []).forEach(m => {
            if (m?.email) emails.add(String(m.email).toLowerCase());
            if (m?.user_id != null) userIds.add(String(m.user_id));
            if (m?.id != null) userIds.add(String(m.id));
        });
        return { emails, userIds };
    }, [diagramMembers]);

    const autocompleteItems = React.useMemo(() => {
        const query = (value || '').trim().toLowerCase();
        return (workspaceMembers || []).filter(m => {
            if (!m) return false;
            const memberEmail = m.email ? String(m.email).toLowerCase() : '';
            const memberUserId = m.user_id != null ? String(m.user_id) : '';
            if (yourId != null && (memberUserId === String(yourId) || (m.id != null && String(m.id) === String(yourId)))) return false;
            if (memberEmail && diagramMemberKeys.emails.has(memberEmail)) return false;
            if (memberUserId && diagramMemberKeys.userIds.has(memberUserId)) return false;
            if (!query) return true;
            const name = (m.name || '').toLowerCase();
            return name.includes(query) || memberEmail.includes(query);
        });
    }, [workspaceMembers, diagramMemberKeys, value, yourId]);

    // Successful invite: show the 2s "Invited ✓" state and clear the input.
    const handleInviteSuccess = () => {
        setIsValidating(false);
        setValue('');
        setIsValidationStarted(false);
        setIsSubmitting(true);
        setTimeout(() => {
            setIsSubmitting(false);
        }, 1000);
    };

    // Failed invite: the saga already showed an error notification. Keep the
    // typed email, drop the spinner, and refocus so the user can retry — never
    // show "Invited ✓".
    const handleInviteError = () => {
        setIsValidating(false);
        setNeedsFocus(true);
    };

    const handleSubmit = () => {
        if (isValidating || isSubmitting) return;

        // Enter a 280ms "validation loading" window (mirrors SignIn.jsx): the
        // button shows a spinner and the input is locked. Clear any prior error
        // so it doesn't flash under the spinner — and so re-setting it below is
        // always a false→true transition that re-fires the refocus effect.
        setIsValidating(true);
        setIsInvalidEmail(false);

        setTimeout(() => {
            // Invalid email — stop the spinner and surface the error.
            if (!isValidInviteEmail(value)) {
                setIsValidating(false);
                setIsInvalidEmail(true);
                setIsValidationStarted(true);
                // Focus is restored by the isInvalidEmail effect, which runs
                // after the input re-enables on the next render.
                return;
            }

            // Valid email — keep the spinner up while the request is in flight.
            // The saga reports the outcome through these callbacks, so "Invited ✓"
            // and the input clear only happen on a real success response.
            onInvite?.(String(value).trim(), selectedRole, {
                onSuccess: handleInviteSuccess,
                onError: handleInviteError,
            });
        }, 280);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleInputFocus = () => {
        setIsAutocompleteOpen(true);
        setIsMenuOpen(false);
    };

    const handleInputChange = (e) => {
        const nextValue = e.target.value;
        setValue(nextValue);
        if (!isAutocompleteOpen) setIsAutocompleteOpen(true);


        if (isValidationStarted) {
            setIsInvalidEmail(false);
            setIsValidationStarted(false);
            if (inputRef.current) inputRef.current.focus();
            return;
        }
        // if (isValidationStarted) {
        //     setIsInvalidEmail(!isValidInviteEmail(nextValue));
        // }
    };

    const handleAutocompleteItemClick = (member) => {
        setValue(member?.email || '');
        setIsAutocompleteOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const showAutocomplete = isAutocompleteOpen && autocompleteItems.length > 0;

    // Email already on this diagram (member) or already invited to it — inviting
    // again is a no-op, so we surface a note instead of the workspace helper.
    const alreadyMember = !isInvalidEmail && isExistingMember(value, diagramMembers);
    const alreadyInvited = !isInvalidEmail && isPendingInvite(value, diagramInvites);
    const showWorkspaceHelper = !isInvalidEmail && !alreadyMember && !alreadyInvited
        && isNewWorkspaceEmail(value, workspaceMembers, diagramInvites);
    return (
        <div className='invite-control'>
            <div className='invite-control-row'>
                <div className='input-with-role'>
                    <input
                        maxLength="150"
                        ref={inputRef}
                        autoFocus={(window.innerWidth >= 1000)}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        inputMode="email"
                        name="email"
                        type="email"
                        // placeholder="Enter email or member name"
                        // placeholder="Enter email address"
                        placeholder={t('m_diagram_share.email_placeholder')}
                        value={value}
                        disabled={isValidating}
                        onChange={handleInputChange}
                        // onFocus={handleInputFocus}
                        onClick={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        className={`input-with-role-input ${isInvalidEmail ? 'invalid' : ''}`}
                    ></input>
                    <button
                        ref={btnMenuRef}
                        className={`input-with-role-button ${isMenuOpen ? 'opened' : ''}`}
                        type="button"
                        onClick={(e) => handleBtnMenu(e)}
                    >
                        {buildRoleName(selectedRole, plan, t)}
                    </button>
                    {isMenuOpen && (
                        // TODO: editadmin
                        <ul className={`input-with-role_menu ${plan === 'free' ? 'small' : ''}`}
                            // <ul className={`input-with-role_menu`}
                            ref={menuRef}>
                            {menuItems.map((item, index) => {
                                if (item === 'line') {
                                    return (<div className="horizontal-line"></div>);
                                } else {
                                    const locked = !isShareRoleAllowed(plan, item);
                                    return <li key={index} className={`input-with-role-item${locked ? ' locked' : ''}`}>
                                        <button
                                            type="button"
                                            className={`input-with-role-item-btn ${item === selectedRole ? 'selected' : ''} ${locked ? 'locked' : ''}`}
                                            onClick={() => handleMenuItemClick(item)}
                                        >
                                            {item === 'member' ? <svg className="edit" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.28 2 7.12 2 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.72 20 18.88 20 17.2V13M8 16h1.675c.489 0 .733 0 .963-.055.204-.05.4-.13.579-.24.201-.123.374-.296.72-.642L21.5 5.5a2.121 2.121 0 0 0-3-3l-9.563 9.563c-.346.346-.519.519-.642.72a2 2 0 0 0-.24.579c-.055.23-.055.474-.055.963z"></path></svg> : ''}
                                            {item === 'admin' ? <svg className="icon-editadmin" width="18px" height="18px" viewBox="0 0 24 24" fill="none"><g> <path fillRule="evenodd" clipRule="evenodd" d="M5 4C4.44772 4 4 4.44772 4 5V19C4 19.5523 4.44772 20 5 20H12H13C13.5523 20 14 19.5523 14 19V5C14 4.44772 13.5523 4 13 4H5ZM5 22H12H13H19C20.6569 22 22 20.6569 22 19V9C22 7.34315 20.6569 6 19 6H16V5C16 3.34315 14.6569 2 13 2H5C3.34315 2 2 3.34315 2 5V19C2 20.6569 3.34315 22 5 22ZM19 20H15.8293C15.9398 19.6872 16 19.3506 16 19V8H19C19.5523 8 20 8.44772 20 9V19C20 19.5523 19.5523 20 19 20ZM7 14H5V16H7V14ZM8 14H10V16H8V14ZM13 14H11V16H13V14ZM17 14H19V16H17V14ZM19 10H17V12H19V10ZM5 10H7V12H5V10ZM10 10H8V12H10V10ZM11 10H13V12H11V10ZM7 6H5V8H7V6ZM8 6H10V8H8V6ZM13 6H11V8H13V6Z" fill="currentColor"></path> </g></svg> : ''}
                                            {item === 'edit' ? <svg className="edit" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.28 2 7.12 2 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.72 20 18.88 20 17.2V13M8 16h1.675c.489 0 .733 0 .963-.055.204-.05.4-.13.579-.24.201-.123.374-.296.72-.642L21.5 5.5a2.121 2.121 0 0 0-3-3l-9.563 9.563c-.346.346-.519.519-.642.72a2 2 0 0 0-.24.579c-.055.23-.055.474-.055.963z"></path></svg> : ''}
                                            {item === 'comment' ? <svg className="comment" width="15px" height="24px" viewBox="0 -2 32 32"><g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd" transform="translate(-152.000000, -255.000000)"><path fill="currentColor" stroke="currentColor" d="M168,281 C166.832,281 165.704,280.864 164.62,280.633 L159.912,283.463 L159.975,278.824 C156.366,276.654 154,273.066 154,269 C154,262.373 160.268,257 168,257 C175.732,257 182,262.373 182,269 C182,275.628 175.732,281 168,281 L168,281 Z M168,255 C159.164,255 152,261.269 152,269 C152,273.419 154.345,277.354 158,279.919 L158,287 L165.009,282.747 C165.979,282.907 166.977,283 168,283 C176.836,283 184,276.732 184,269 C184,261.269 176.836,255 168,255 L168,255 Z M175,266 L161,266 C160.448,266 160,266.448 160,267 C160,267.553 160.448,268 161,268 L175,268 C175.552,268 176,267.553 176,267 C176,266.448 175.552,266 175,266 L175,266 Z M173,272 L163,272 C162.448,272 162,272.447 162,273 C162,273.553 162.448,274 163,274 L173,274 C173.552,274 174,273.553 174,273 C174,272.447 173.552,272 173,272 L173,272 Z"></path></g></svg> : ''}
                                            {item === 'view' ? <svg className="view" width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"></circle> <path d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z" stroke="currentColor" strokeWidth="1.5"></path> </g></svg> : ''}
                                            {buildRoleName(item, plan, t)}
                                            {/* {locked ? <LockBadge /> : null} */}
                                        </button>
                                    </li>
                                }
                            })}
                        </ul>
                    )}
                    {showAutocomplete && (
                        <ul className="input-autocomplete_menu" ref={autocompleteRef}>
                            {autocompleteItems.map((member, index) => (
                                <li key={`ac-${member.id ?? member.user_id ?? member.email ?? index}`} className="input-autocomplete-item">
                                    <button
                                        type="button"
                                        className="input-autocomplete-item-btn"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleAutocompleteItemClick(member)}
                                    >
                                        <div className="input-autocomplete-item-avatar">
                                            {member.image
                                                ? <img className="input-autocomplete-item-avatar-image" src={getAvatarUrl(member.image, 60)} width="28" height="28" referrerPolicy="no-referrer" alt="" />
                                                : <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="-0.5 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                            }
                                        </div>
                                        <div className="input-autocomplete-item-text">
                                            <span className="input-autocomplete-item-name">{member.name || member.email}</span>
                                            {member.email && member.name ? <span className="input-autocomplete-item-email">{member.email}</span> : null}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <button
                    disabled={isValidating || isSubmitting}
                    className={`invite-button ${isValidating ? 'loading' : ''} ${isSubmitting ? 'invited' : ''}`}
                    onClick={handleSubmit}
                >
                    {isValidating
                        ? <LoaderSimple />
                        : isSubmitting
                            ? <React.Fragment>
                                {/* <svg width="18px" height="26px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M4 12L8.94975 16.9497L19.5572 6.34326" stroke="#29d1ac" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"></path></g></svg> */}
                                <div>{t('b.invited')}</div>
                            </React.Fragment>
                            : <div>{t('b.invite')}</div>
                    }
                </button>
            </div>
            {isInvalidEmail && value !== ''
                ? <p className='invite-control-message invite-control-error'>{t('n.invalid_email')}</p>
                : alreadyMember
                    ? <p className='invite-control-message invite-control-helper'>{t('m_diagram_share.email_already_in_list')}</p>
                    : alreadyInvited
                        ? <p className='invite-control-message invite-control-helper'>{t('m_diagram_share.email_already_in_list')}</p>
                        : showWorkspaceHelper
                            ? <p className='invite-control-message invite-control-helper'>{t('m_diagram_share.will_be_invited_to_workspace')}</p>
                            : null}
        </div>
    )
};
